import { Dispatch, FC, SetStateAction, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import { Email, Key } from '@mui/icons-material'
import { Box, Paper, Typography } from '@mui/material'


import { useDispatch } from 'react-redux'
import { AppDispatch } from 'redux/store'
import { setUser } from 'redux/ui/common/userUI'

import { usePOST_authLoginMutation } from 'redux/api/authApi'
import { useGET_userQuery } from 'redux/api/userAPI'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import InputField from 'components/atoms/fields/InputField'

import { useErrorAlert } from 'utils/customHooks/useErrorAlert'
import { useSuccessAlert } from 'utils/customHooks/useSuccessAlert'
import { useMedia } from 'utils/customHooks/useMedia'
import { ELEMENT_RADIUS } from 'utils/customizedTheme'
import formModel, { initialValues, validationSchema } from 'utils/formModels/loginModel'

import logo from 'assets/svg/logo.svg'
import welcome from 'assets/sound/welcome.mp3'

import { AuthType, LocationState } from 'types'

interface IProps {
  setAuthState: Dispatch<SetStateAction<AuthType>>
}

const ResetPasswordForm: FC<IProps> = ({ setAuthState }) => {
  const { t } = useTranslation()
  const { isUnder1200px } = useMedia()

  const dispatch = useDispatch<AppDispatch>()

  const navigate = useNavigate()
  const location = useLocation() as LocationState
  const from = location.state?.from?.pathname || '/'
  const userToken = window.localStorage.getItem('token')

  const audioEl = useRef(new Audio(welcome));

  const { email, password } = formModel()

  // Get authentication token
  const [POST_authLogin, { isLoading: authDataLoading, error }] = usePOST_authLoginMutation()
  useErrorAlert(error)
  
  // Get user authorization data only if authentication token is set in Redux store
  const {
    isFetching: userDataLoading,
    data: userData,
    error: userDataError,
  } = useGET_userQuery(null, { skip: !userToken })
  
  useErrorAlert(userDataError)
  useSuccessAlert(userData ? `${t('alerts.login-success', {user: `${userData.first_name} ${userData.last_name}`})}` : '')

  useEffect(() => {
    // Redirect already authenticated users to the app
    if (userData) {
      dispatch(setUser(userData))
      const audio = audioEl.current;
      audio.play();
      navigate(from, { replace: true })
    }
  }, [userData])

  return (
    <Paper
      elevation={0}
      sx={{
        background: 'none',
        borderRadius: ELEMENT_RADIUS,
        overflow: 'hidden',
        marginTop: '-3rem',
        width: isUnder1200px ? '90%' : '30rem',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        component='img'
        sx={{
          height: 'auto',
          width: '10rem',
          margin: '0 auto',
        }}
        alt='EsyBee'
        src={logo}
      />
      <Typography
        variant='h2'
        sx={{ textAlign: 'center', fontSize: '3rem', fontWeight: '200', marginTop: '3rem' }}
      >
        {`${t('common.welcome')}!`}
      </Typography>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values) => POST_authLogin(values)}
      >
        <Form autoComplete={'off'}>
          <Box sx={{ padding: '1.5rem' }}>
            <Typography sx={{ fontWeight: '300', margin: '1rem auto' }}>{t('login.secure-page')}</Typography>
            <InputField
              fieldProps={{ ...email }}
              inputProps={{'autoComplete': 'off'}}
              icon={<Email color='primary' sx={{ mr: 1, my: 0.5 }} />}
            />
            <InputField
              fieldProps={{ ...password }}
              inputProps={{'autoComplete': 'off'}}
              icon={<Key color='primary' sx={{ mr: 1, my: 0.5 }} />}
              sx={{ marginTop: '2rem' }}
            />
            <SubmitButton
              text={t('common.login')}
              sx={{ marginTop: '2.5rem', '& button': {minHeight: '3rem'} }}
              color='primary'
              isLoading={authDataLoading || userDataLoading}
            />
          </Box>
        </Form>
      </Formik>
      <Typography
        onClick={() => setAuthState('reset')}
        sx={{
          fontSize: '0.8rem',
          color: '#9fa0a4',
          textAlign: 'center',
          margin: '-0.5rem 0 0.7rem',
          cursor: 'pointer',
          '&:hover': { textDecoration: 'underline', color: 'inherit' },
        }}
      >
        {t('login.lost-password')}
      </Typography>
    </Paper>
  )
}
export default ResetPasswordForm
